import {Button, Toolbar, ToolbarItem, ToolbarSpacer} from "@progress/kendo-react-buttons"
import {Field, FieldRenderProps, Form, FormElement, FormRenderProps} from "@progress/kendo-react-form"
import {Input, InputChangeEvent} from "@progress/kendo-react-inputs"
import {Error, FloatingLabel} from "@progress/kendo-react-labels"
import {
    MDBBtn,
    MDBCol,
    MDBModal,
    MDBModalBody,
    MDBModalContent,
    MDBModalDialog,
    MDBModalHeader,
    MDBModalTitle,
    MDBRow,
    MDBTypography,
} from "mdbreact"
import PropTypes from "prop-types"
import {useCallback, useEffect, useRef, useState} from "react"
import {RootState} from "../../../rootReducer"
import {SpinnerFragment} from "../../../shared/CommonItems"
import {useAppDispatch, useAppSelector} from "../../../store"
import AlertToastr from "../../Shared/alertToastr"
import {PermissionsChecker} from "../../Shared/permissionChecker"
import DeleteContactModal from "../CustomerContactGrid/DeleteContactModal"
import CustomerContactAddEdit from "../CustomerContactGrid/index"
import {addCustomer, Customer, editCustomer, toggleAddEdit} from "./../../../containers/Customer/customerSlice"
import {BookingDetail} from "../../../containers/MotBooking/bookingSlice"

const EmailInput = (fieldRenderProps: FieldRenderProps) => {
    const {validationMessage, visited, label, id, valid, value, disabled, ...others} = fieldRenderProps
    const showValidationMessage = visited && validationMessage
    return (
        <>
            <FloatingLabel
                label={label}
                editorValue={value}
                editorValid={valid}
                editorDisabled={disabled}
                editorId={id}
            >
                <Input value={value} valid={valid} type={"email"} id={id} disabled={disabled} {...others} />
            </FloatingLabel>
            {showValidationMessage && <Error>{validationMessage}</Error>}
        </>
    )
}

function getKeyboardFocusableElement(): HTMLElement | undefined {
    const content = document.getElementsByClassName("modal-content")[0]
    if (content) {
        const focusableElements = (
            [content?.querySelectorAll("input, textarea, select")] as NodeListOf<HTMLElement>[]
        ).filter(el => !el[0]?.hasAttribute("disabled") && !el[0]?.getAttribute("aria-hidden"))[0]

        return focusableElements[1]
    }
}

const AddEditCustomer = () => {
    const dispatch = useAppDispatch()
    const formRef = useRef<any>()

    const customer = useAppSelector((state: RootState) => state.containers.customer.customer)
    const loadingCustDetails = useAppSelector((state: RootState) => state.containers.customer.customerDetailloading)
    const isAddEditOpen = useAppSelector((state: RootState) => state.containers.customer.addEditOpen)
    const showDltPopover = useAppSelector((state: RootState) => state.containers.customer.showDeletePopover)
    const customerContacId = useAppSelector((state: RootState) => state.containers.customer.customerContacId)

    const handleSubmit = (e: any) => {
        const customerDetail: Customer = {...e}
        customerDetail.accountNo = customerDetail.accountNo.trim()
        customerDetail.companyName = customerDetail.companyName.trim()
        customerDetail.customerId === 0 ? dispatch(addCustomer(customerDetail)) : dispatch(editCustomer(customerDetail))
    }

    useEffect(() => {
        getKeyboardFocusableElement()?.focus()
    }, [isAddEditOpen, customer])

    window.addEventListener("keydown", event => event.stopPropagation(), true)

    const accountNumberRegex = new RegExp(/^[0]/)

    const accountValidator = (value: string) =>
        !value || value.trim().length === 0
            ? "Account number is required"
            : accountNumberRegex.test(value)
            ? "Account number should not start with 0"
            : ""

    const validateName = (value: string) => (value.trim().length > 0 ? "" : "Please enter valid name")

    const disableCustomerInput = customer.source === "S"

    return (
        <>
            <AlertToastr />
            <MDBModal staticBackdrop animationDirection="right" show={isAddEditOpen} tabIndex="-1">
                <MDBModalDialog position="top right modal-xl" side>
                    <MDBModalContent className="full-height">
                        <MDBModalHeader>
                            <MDBModalTitle>Customer</MDBModalTitle>
                            <MDBBtn
                                color="none"
                                className="btn-close"
                                onClick={() => dispatch(toggleAddEdit(false))}
                            ></MDBBtn>
                        </MDBModalHeader>
                        {loadingCustDetails && <SpinnerFragment />}
                        {isAddEditOpen && (
                            <Form
                                ref={formRef}
                                key={JSON.stringify(customer)}
                                initialValues={customer}
                                onSubmit={handleSubmit}
                                render={(formRenderProps: FormRenderProps) => (
                                    <FormElement className="w-100">
                                        <Toolbar className="shadow-0 border-bottom p-0 pb-1 pt-1 position-sticky top-0">
                                            <ToolbarItem>
                                                <PermissionsChecker permission={["Customers.Add", "Customers.Edit"]}>
                                                    <Button
                                                        disabled={disableCustomerInput}
                                                        type="submit"
                                                        fillMode="flat"
                                                        themeColor={"primary"}
                                                        iconClass="fa fa-save"
                                                    >
                                                        Save
                                                    </Button>
                                                </PermissionsChecker>
                                            </ToolbarItem>
                                            <ToolbarSpacer />
                                            {customer.customerId > 0 && (
                                                <ToolbarItem>
                                                    <MDBTypography tag="h6" className="m-1">
                                                        Source: {customer.source === "S" ? "System" : "Manual"}
                                                    </MDBTypography>
                                                </ToolbarItem>
                                            )}
                                        </Toolbar>
                                        <MDBModalBody className="modal-overflow">
                                            <fieldset className={"k-form-fieldset mt-0"}>
                                                <Input name="customerId" type="hidden" />
                                                <MDBRow>
                                                    <MDBCol md="4">
                                                        <Field
                                                            required
                                                            name="accountNo"
                                                            component={Input}
                                                            label="Account No"
                                                            disabled={disableCustomerInput}
                                                            validator={accountValidator}
                                                        />
                                                    </MDBCol>
                                                    <MDBCol md="8">
                                                        <Field
                                                            required
                                                            name="companyName"
                                                            component={Input}
                                                            label="Name"
                                                            disabled={disableCustomerInput}
                                                            validator={validateName}
                                                        />
                                                    </MDBCol>
                                                </MDBRow>
                                                <MDBRow>
                                                    <MDBCol md="12">
                                                        <Field
                                                            required
                                                            name="address1"
                                                            component={Input}
                                                            label="Address"
                                                            disabled={disableCustomerInput}
                                                        />
                                                    </MDBCol>
                                                </MDBRow>
                                                <MDBRow>
                                                    <MDBCol md="12">
                                                        <Field
                                                            name="address2"
                                                            component={Input}
                                                            label="Address2"
                                                            disabled={disableCustomerInput}
                                                        />
                                                    </MDBCol>
                                                </MDBRow>
                                                <MDBRow>
                                                    <MDBCol md="12">
                                                        <Field
                                                            name="address3"
                                                            component={Input}
                                                            label="Address3"
                                                            disabled={disableCustomerInput}
                                                        />
                                                    </MDBCol>
                                                </MDBRow>
                                                <MDBRow>
                                                    <MDBCol md="6">
                                                        <Field
                                                            name="city"
                                                            component={Input}
                                                            label={"City"}
                                                            disabled={disableCustomerInput}
                                                        />
                                                    </MDBCol>
                                                    <MDBCol md="6">
                                                        <Field
                                                            name="postCode"
                                                            component={Input}
                                                            label="Post Code"
                                                            disabled={disableCustomerInput}
                                                        />
                                                    </MDBCol>
                                                </MDBRow>
                                                <MDBRow>
                                                    <MDBCol md="6">
                                                        <Field
                                                            name="telephone1"
                                                            component={Input}
                                                            label="Phone"
                                                            disabled={disableCustomerInput}
                                                        />
                                                    </MDBCol>
                                                    <MDBCol md="6">
                                                        <Field
                                                            name="telephone2"
                                                            component={Input}
                                                            label="Phone 2"
                                                            disabled={disableCustomerInput}
                                                        />
                                                    </MDBCol>
                                                </MDBRow>
                                                <MDBRow>
                                                    <MDBCol md="6">
                                                        <Field
                                                            name="email"
                                                            type="email"
                                                            component={EmailInput}
                                                            label="E-Mail"
                                                            // validator={emailValidator}
                                                            disabled={disableCustomerInput}
                                                        />
                                                    </MDBCol>
                                                    <MDBCol md="6">
                                                        <Field
                                                            name="web"
                                                            component={Input}
                                                            label="WebSite"
                                                            disabled={disableCustomerInput}
                                                        />
                                                    </MDBCol>
                                                </MDBRow>
                                                <MDBRow>
                                                    <MDBCol md="12">
                                                        <Field
                                                            name="contactName"
                                                            component={Input}
                                                            label="Contact Name"
                                                            disabled={disableCustomerInput}
                                                        />
                                                    </MDBCol>
                                                </MDBRow>
                                            </fieldset>
                                            {customer.customerId > 0 && (
                                                <div className="mt-4">
                                                    <MDBRow>
                                                        <MDBCol md="12">
                                                            <CustomerContactAddEdit
                                                                customerId={customer.customerId}
                                                                enableEdit={true}
                                                                booking={{} as BookingDetail}
                                                                onBlur={() => {}}
                                                            />
                                                        </MDBCol>
                                                    </MDBRow>
                                                </div>
                                            )}
                                        </MDBModalBody>
                                        <input
                                            style={{position: "absolute", top: "-10000px"}}
                                            name="lockFocus"
                                            id="lockFocus"
                                            onFocus={() => getKeyboardFocusableElement()?.focus()}
                                        />
                                    </FormElement>
                                )}
                            />
                        )}
                    </MDBModalContent>
                </MDBModalDialog>
            </MDBModal>
            <DeleteContactModal
                showDltPopover={showDltPopover}
                customerContactId={customerContacId}
                customerId={customer.customerId}
            />
        </>
    )
}

AddEditCustomer.prototype = {
    toggleCustomerDialog: PropTypes.bool,
    setOpenCloseCustomerDialog: PropTypes.bool,
    newCustomerDetails: PropTypes.func,
}
export default AddEditCustomer
